import React, { useState } from 'react';
import { Alert, App, Button, Card, Empty, Radio, Space, Spin, Tag } from 'antd';
import {
  RELEASE_NOTE_HISTORY_PAGE_SIZE,
  useCreateReleaseNote,
  useDeleteReleaseNote,
  useReleaseNoteHistoryPage,
  useReleaseNotes,
  useUpdateReleaseNote,
} from '@/api/ReleaseNotes';
import { useAppCustomers, useHttpAuthReady } from '@/hooks';
import { useTranslate } from '@/translations';
import {
  isReleaseNotePublished,
  ReleaseNote,
  ReleaseNoteApp,
  SaveReleaseNote,
} from '@/types';
import {
  Icon,
  PageContainer,
  Title,
  faEyeSlash,
  faFileLines,
  faGlobe,
  faPen,
  faPlus,
  faTrash,
} from '@/ui/components';
import { ReleaseNoteEditorPopup } from './ReleaseNoteEditorPopup';
import dayjs from 'dayjs';

export const ReleaseNotes: React.FC = () => {
  const { t } = useTranslate();
  const { message, modal } = App.useApp();
  const { hasSuperAdminAccess } = useAppCustomers();
  const isHttpAuthReady = useHttpAuthReady();
  const [take, setTake] = useState(RELEASE_NOTE_HISTORY_PAGE_SIZE);
  const [appFilter, setAppFilter] = useState<ReleaseNoteApp | 'all'>('all');
  const historyQuery = useReleaseNoteHistoryPage(take, {
    enabled: isHttpAuthReady && !hasSuperAdminAccess,
  });
  const adminQuery = useReleaseNotes(
    appFilter === 'all' ? undefined : appFilter,
    {
      enabled: isHttpAuthReady && hasSuperAdminAccess,
    },
  );
  const { mutateAsync: createNote, isPending: creating } =
    useCreateReleaseNote();
  const { mutateAsync: updateNote, isPending: updating } =
    useUpdateReleaseNote();
  const { mutateAsync: deleteNote, isPending: deleting } =
    useDeleteReleaseNote();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<ReleaseNote | null>(null);

  const activeQuery = hasSuperAdminAccess ? adminQuery : historyQuery;
  const notes = hasSuperAdminAccess
    ? (adminQuery.data ?? [])
    : (historyQuery.data?.items ?? []);
  const hasMore = hasSuperAdminAccess
    ? false
    : Boolean(historyQuery.data?.hasMore);
  const loading = !isHttpAuthReady || activeQuery.isLoading;
  const { isFetching, isError } = activeQuery;

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingNote(null);
  };

  const openCreate = () => {
    setEditingNote(null);
    setEditorOpen(true);
  };

  const openEdit = (note: ReleaseNote) => {
    setEditingNote(note);
    setEditorOpen(true);
  };

  const save = async (values: SaveReleaseNote) => {
    try {
      const res = editingNote
        ? await updateNote({ id: editingNote.id, data: values })
        : await createNote(values);
      if (!res.success) {
        message.error(t('releaseNotesSaveFailed'));
        return;
      }
      message.success(
        values.publish === true
          ? t('releaseNotesPublished')
          : values.publish === false
            ? t('releaseNotesUnpublished')
            : t('releaseNotesSaved'),
      );
      closeEditor();
    } catch {
      message.error(t('releaseNotesSaveFailed'));
    }
  };

  const setPublished = async (note: ReleaseNote, publish: boolean) => {
    try {
      const res = await updateNote({
        id: note.id,
        data: {
          app: note.app,
          versionLabel: note.versionLabel,
          title: note.title,
          notes: note.notes,
          publish,
        },
      });
      if (!res.success) {
        message.error(t('releaseNotesSaveFailed'));
        return;
      }
      message.success(
        publish ? t('releaseNotesPublished') : t('releaseNotesUnpublished'),
      );
    } catch {
      message.error(t('releaseNotesSaveFailed'));
    }
  };

  const confirmDelete = (note: ReleaseNote) => {
    modal.confirm({
      title: t('confirm'),
      content: t('deleteRecordQuestion'),
      okText: t('yes'),
      onOk: async () => {
        try {
          const res = await deleteNote(note.id);
          if (!res.success) {
            message.error(t('releaseNotesDeleteFailed'));
            return;
          }
          message.success(t('releaseNotesDeleted'));
        } catch {
          message.error(t('releaseNotesDeleteFailed'));
        }
      },
    });
  };

  return (
    <PageContainer>
      <Title title={t('releaseNotes')} icon={faFileLines} />

      <Card>
        <Spin spinning={loading || deleting || updating}>
          <Space direction="vertical" size="large" className="w-100">
            {hasSuperAdminAccess ? (
              <div className="d-flex align-items-center justify-content-between">
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  value={appFilter}
                  aria-label={t('releaseNotesApp')}
                  onChange={(event) =>
                    setAppFilter(event.target.value as ReleaseNoteApp | 'all')
                  }
                  options={[
                    { value: 'all', label: t('releaseNotesAppAll') },
                    { value: 'OneView', label: 'OneView' },
                    { value: 'TMS', label: 'TMS' },
                  ]}
                />
                <Button
                  shape="circle"
                  title={t('releaseNotesAdd')}
                  aria-label={t('releaseNotesAdd')}
                  onClick={openCreate}
                >
                  <Icon icon={faPlus} />
                </Button>
              </div>
            ) : null}
            {isError ? (
              <Alert
                type="error"
                showIcon
                message={t('releaseNotesHistoryFailed')}
              />
            ) : null}
            {notes.length === 0 && !loading && !isError ? (
              <Empty description={t('releaseNotesEmpty')} />
            ) : null}
            {notes.map((note) => (
              <article key={`${note.app}-${note.versionLabel}-${note.id}`}>
                <div className="d-flex align-items-center justify-content-between">
                  <h3 className="m-0 fw-bold d-flex align-items-center gap-2">
                    <span>
                      {note.versionLabel} - {note.title}
                      {isReleaseNotePublished(note) && note.publishedAt
                        ? ` · ${dayjs(note.publishedAt).format('MMM D')}`
                        : ''}
                    </span>
                    {hasSuperAdminAccess ? (
                      <>
                        <Tag
                          color={
                            note.app === 'OneView' ? 'magenta' : 'geekblue'
                          }
                        >
                          {note.app}
                        </Tag>
                        <Tag
                          color={
                            isReleaseNotePublished(note) ? 'success' : 'warning'
                          }
                        >
                          {t(
                            isReleaseNotePublished(note)
                              ? 'releaseNotesTagPublished'
                              : 'releaseNotesTagDraft',
                          )}
                        </Tag>
                      </>
                    ) : null}
                  </h3>
                  {hasSuperAdminAccess ? (
                    <div className="d-flex align-items-center">
                      {isReleaseNotePublished(note) ? (
                        <Button
                          type="link"
                          title={t('releaseNotesUnpublish')}
                          icon={<Icon icon={faEyeSlash} />}
                          onClick={() => setPublished(note, false)}
                        />
                      ) : (
                        <Button
                          type="link"
                          title={t('releaseNotesPublish')}
                          icon={<Icon icon={faGlobe} />}
                          onClick={() => setPublished(note, true)}
                        />
                      )}
                      <Button
                        type="link"
                        title={t('edit')}
                        icon={<Icon icon={faPen} />}
                        onClick={() => openEdit(note)}
                      />
                      <Button
                        type="link"
                        color="danger"
                        variant="text"
                        title={t('delete')}
                        icon={<Icon icon={faTrash} />}
                        onClick={() => confirmDelete(note)}
                      />
                    </div>
                  ) : null}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: note.notes,
                  }}
                />
              </article>
            ))}
            {hasMore ? (
              <Button
                onClick={() =>
                  setTake((current) => current + RELEASE_NOTE_HISTORY_PAGE_SIZE)
                }
                loading={isFetching && !activeQuery.isLoading}
              >
                {t('releaseNotesLoadMore')}
              </Button>
            ) : null}
          </Space>
        </Spin>
      </Card>

      {hasSuperAdminAccess ? (
        <ReleaseNoteEditorPopup
          open={editorOpen}
          note={editingNote}
          defaultApp={appFilter === 'all' ? 'OneView' : appFilter}
          saving={creating || updating}
          onClose={closeEditor}
          onSubmit={save}
        />
      ) : null}
    </PageContainer>
  );
};
